import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { getConversionReport, getPipelineReport } from '@/api/reports.api';
import { Banner } from '@/components/common/Banner';
import { StatusChip } from '@/components/common/StatusChip';
import { ProgressBar } from '@/components/common/ProgressBar';
import { AgentCombobox } from '@/components/form/AgentCombobox';
import { ExportButton } from '@/components/table/ExportButton';
import { toneForLeadStatus } from '@/lib/leadStatus';
import type { ConversionReportRow, PipelineReportRow } from '@/types/shared';
import './SalesReportsPage.css';

/** Local YYYY-MM-DD (toISOString would shift the day/month in +GMT zones like Africa/Cairo). */
function isoLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Default report window = the current calendar month (1st → today). */
function firstOfCurrentMonth(): string {
  const now = new Date();
  return isoLocal(new Date(now.getFullYear(), now.getMonth(), 1));
}
function today(): string {
  return isoLocal(new Date());
}

export function SalesReportsPage() {
  const { t } = useTranslation();

  const [from, setFrom] = useState(firstOfCurrentMonth);
  const [to, setTo] = useState(today);
  const [employeeId, setEmployeeId] = useState('');
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);

  const [conversionRows, setConversionRows] = useState<ConversionReportRow[] | null>(null);
  const [conversionLoading, setConversionLoading] = useState(true);
  const [conversionError, setConversionError] = useState<string | null>(null);
  // The from/to that actually produced `conversionRows` — kept separate from the (possibly
  // edited-but-not-yet-submitted) `from`/`to` form state so the export always matches what's on
  // screen, not whatever the user is mid-typing in the date fields.
  const [appliedRange, setAppliedRange] = useState({ from: firstOfCurrentMonth(), to: today() });

  const [pipelineRows, setPipelineRows] = useState<PipelineReportRow[] | null>(null);
  const [pipelineLoading, setPipelineLoading] = useState(true);
  const [pipelineError, setPipelineError] = useState<string | null>(null);
  const [pipelineReloadToken, setPipelineReloadToken] = useState(0);

  function loadConversion(params: { from: string; to: string; employeeId?: string }) {
    setConversionLoading(true);
    setConversionError(null);
    getConversionReport(params)
      .then((rows) => {
        setConversionRows(rows);
        setAppliedRange({ from: params.from, to: params.to });
      })
      .catch(() => setConversionError(t('reports.conversion.error')))
      .finally(() => setConversionLoading(false));
  }

  // Initial load with the default 30-day window.
  useEffect(() => {
    loadConversion({ from, to });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    setPipelineLoading(true);
    setPipelineError(null);
    getPipelineReport()
      .then((rows) => {
        if (!cancelled) setPipelineRows(rows);
      })
      .catch(() => {
        if (!cancelled) setPipelineError(t('reports.pipeline.error'));
      })
      .finally(() => {
        if (!cancelled) setPipelineLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t, pipelineReloadToken]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setDateRangeError(null);

    if (!from || !to) {
      setDateRangeError(t('reports.dateRangeRequired'));
      return;
    }
    if (from > to) {
      setDateRangeError(t('reports.dateRangeInvalid'));
      return;
    }

    loadConversion({ from, to, employeeId: employeeId || undefined });
  }

  const conversionTotals = (conversionRows ?? []).reduce(
    (acc, row) => ({
      assigned: acc.assigned + row.leadsAssigned,
      registered: acc.registered + row.leadsRegistered,
    }),
    { assigned: 0, registered: 0 },
  );

  return (
    <section className="fa-sales-reports">
      <h1 className="fa-sales-reports__title">{t('reports.title')}</h1>

      <section className="fa-form-section fa-sales-reports__section">
        <div className="fa-sales-reports__section-header">
          <h2 className="fa-form-section__title">{t('reports.conversion.title')}</h2>
          <ExportButton
            config={{
              endpoint: '/reports/sales/export',
              // `ExportSalesReportQuery` only accepts `from`/`to` (no `employeeId` narrowing —
              // the export always covers every Sales Agent in the window). Uses the *applied*
              // range (what produced the table currently on screen), not any unsubmitted edits
              // still sitting in the date inputs.
              params: { from: appliedRange.from, to: appliedRange.to },
              fallbackFilename: 'SalesReport_export.xlsx',
            }}
          />
        </div>
        <p className="fa-form-section__description">{t('reports.conversion.description')}</p>

        <form noValidate className="fa-sales-reports__filters" onSubmit={handleSubmit}>
          <div className="fa-field">
            <label htmlFor="report-from">{t('reports.conversion.from')}</label>
            <input
              id="report-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="fa-field">
            <label htmlFor="report-to">{t('reports.conversion.to')}</label>
            <input id="report-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="fa-field">
            <label htmlFor="report-employeeId">{t('reports.conversion.employeeIdLabel')}</label>
            <AgentCombobox
              id="report-employeeId"
              value={employeeId}
              onChange={setEmployeeId}
              placeholder={t('reports.conversion.employeeSearch')}
              clearLabel={t('reports.conversion.employeeAll')}
            />
          </div>
          <div className="fa-field fa-sales-reports__submit-field">
            <button type="submit" className="fa-btn fa-btn--primary" disabled={conversionLoading}>
              {t('reports.conversion.submit')}
            </button>
          </div>
        </form>

        {dateRangeError && (
          <Banner tone="danger" assertive>
            {dateRangeError}
          </Banner>
        )}

        {conversionError && (
          <div className="fa-sales-reports__error" role="alert">
            <p>{conversionError}</p>
            <button
              type="button"
              className="fa-btn fa-btn--secondary"
              onClick={() =>
                loadConversion({ from, to, employeeId: employeeId.trim() || undefined })
              }
            >
              {t('common.retry')}
            </button>
          </div>
        )}

        {conversionLoading ? (
          <p className="fa-form-loading">{t('common.loading')}</p>
        ) : !conversionError && (conversionRows?.length ?? 0) === 0 ? (
          <p className="fa-field__hint">{t('reports.conversion.empty')}</p>
        ) : (
          !conversionError && (
            <div className="fa-data-table__scroll">
              <table className="fa-data-table__table">
                <caption className="fa-visually-hidden">{t('reports.conversion.title')}</caption>
                <thead>
                  <tr>
                    <th scope="col">{t('reports.conversion.columns.employee')}</th>
                    <th scope="col">{t('reports.conversion.columns.leadsAssigned')}</th>
                    <th scope="col">{t('reports.conversion.columns.leadsRegistered')}</th>
                    <th scope="col">{t('reports.conversion.columns.conversionRate')}</th>
                    <th scope="col">{t('reports.conversion.columns.inFollowUp')}</th>
                  </tr>
                </thead>
                <tbody>
                  {conversionRows?.map((row) => (
                    <tr key={row.employee.id}>
                      <td data-label={t('reports.conversion.columns.employee')}>
                        {row.employee.fullName}
                      </td>
                      <td data-label={t('reports.conversion.columns.leadsAssigned')}>
                        <bdi>{row.leadsAssigned}</bdi>
                      </td>
                      <td data-label={t('reports.conversion.columns.leadsRegistered')}>
                        <bdi>{row.leadsRegistered}</bdi>
                      </td>
                      <td data-label={t('reports.conversion.columns.conversionRate')}>
                        <span className="fa-sales-reports__rate-cell">
                          <ProgressBar
                            value={row.conversionRate}
                            max={100}
                            label={t('reports.conversion.rateLabel', {
                              rate: Math.round(row.conversionRate),
                            })}
                          />
                          <bdi>{Math.round(row.conversionRate)}%</bdi>
                        </span>
                      </td>
                      <td data-label={t('reports.conversion.columns.inFollowUp')}>
                        <bdi>{row.inFollowUpCount}</bdi>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {conversionRows && conversionRows.length > 0 && (
                  <tfoot>
                    <tr>
                      <th scope="row">{t('reports.conversion.totalsLabel')}</th>
                      <td>
                        <bdi>{conversionTotals.assigned}</bdi>
                      </td>
                      <td>
                        <bdi>{conversionTotals.registered}</bdi>
                      </td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )
        )}
      </section>

      <section className="fa-form-section fa-sales-reports__section">
        <div className="fa-sales-reports__section-header">
          <h2 className="fa-form-section__title">{t('reports.pipeline.title')}</h2>
          <button
            type="button"
            className="fa-btn fa-btn--secondary"
            onClick={() => setPipelineReloadToken((v) => v + 1)}
            disabled={pipelineLoading}
          >
            {t('common.retry')}
          </button>
        </div>
        <p className="fa-form-section__description">{t('reports.pipeline.description')}</p>

        {pipelineError && (
          <div className="fa-sales-reports__error" role="alert">
            <p>{pipelineError}</p>
          </div>
        )}

        {pipelineLoading ? (
          <p className="fa-form-loading">{t('common.loading')}</p>
        ) : !pipelineError && (pipelineRows?.length ?? 0) === 0 ? (
          <p className="fa-field__hint">{t('reports.pipeline.empty')}</p>
        ) : (
          !pipelineError && (
            <div className="fa-data-table__scroll">
              <table className="fa-data-table__table">
                <caption className="fa-visually-hidden">{t('reports.pipeline.title')}</caption>
                <thead>
                  <tr>
                    <th scope="col">{t('reports.pipeline.columns.employee')}</th>
                    <th scope="col">{t('reports.pipeline.columns.status')}</th>
                    <th scope="col">{t('reports.pipeline.columns.count')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pipelineRows?.map((row) => (
                    <tr key={`${row.employee.id}-${row.status}`}>
                      <td data-label={t('reports.pipeline.columns.employee')}>
                        {row.employee.fullName}
                      </td>
                      <td data-label={t('reports.pipeline.columns.status')}>
                        <StatusChip
                          label={t(`leads.status.${row.status}`)}
                          tone={toneForLeadStatus(row.status)}
                        />
                      </td>
                      <td data-label={t('reports.pipeline.columns.count')}>
                        <bdi>{row.count}</bdi>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </section>
    </section>
  );
}
